import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router';

import { type Notification, useNotificationFeedQuery } from '@entities/notification';
import {
  useReadAllNotificationsMutation,
  useReadNotificationMutation,
} from '@features/notification';
import { appPath } from '@shared/config';
import { ROLE, selectRole, useSession } from '@shared/session';

/** GlobalHeader 의 알림 슬롯을 채우는 쪽에서 쓰는 상태 */
export interface AppNotification {
  open: boolean;
  toggle(): void;
  hasUnread: boolean;
  items: Notification[];
  lastPage: boolean;
  loadingMore: boolean;
  loadMore(): void;
  select(item: Notification): void;
  /** 그 회원의 알림 전부를 읽음으로(낙관적 — 목록 · 빨간 점이 바로 바뀐다). 목록은 열린 채 */
  readAll(): void;
}

/**
 * 헤더 알림: `GET /notification`(커서). 빨간 점과 목록이 같은 query 를 본다.
 * 아이콘을 누르면 열고, 바깥을 누르거나 Esc 면 닫는다. 알림을 누르면 읽음(낙관적) + 그 포트폴리오 상세.
 * 열림 여부만 지역 상태다. 알림은 D3 · Admin 만 받는다(LP · PF 는 헤더에 알림이 없고 API 도 403) — 부르지 않는다.
 */
export function useAppNotification(): AppNotification {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const role = useSession(selectRole);
  const feed = useNotificationFeedQuery({ enabled: role === ROLE.D3 || role === ROLE.ADMIN });
  const read = useReadNotificationMutation();
  const readAll = useReadAllNotificationsMutation();

  useEffect(() => {
    if (!open) return;
    const handlePointerDown = (event: PointerEvent) => {
      if (
        event.target instanceof Element &&
        event.target.closest('[data-slot="header-notification"]')
      ) {
        return;
      }
      setOpen(false);
    };
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false);
    };
    document.addEventListener('pointerdown', handlePointerDown);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('pointerdown', handlePointerDown);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [open]);

  return {
    open,
    toggle: () => setOpen((prev) => !prev),
    hasUnread: feed.data?.hasUnread ?? false,
    items: feed.data?.items ?? [],
    // 첫 쪽을 받는 중에도 "Loading..." 줄을 보인다
    lastPage: !feed.isPending && !feed.hasNextPage,
    loadingMore: feed.isFetchingNextPage,
    loadMore: () => {
      if (feed.hasNextPage && !feed.isFetchingNextPage) void feed.fetchNextPage();
    },
    readAll: () => readAll.mutate(),
    select: (item) => {
      if (!item.isRead) read.mutate(item.id);
      setOpen(false);
      void navigate(appPath.portfolioDetail(item.portfolioId));
    },
  };
}
